import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deletePersonnelTarget,
  getManagerTargetDistribution,
  getPersonnelTargets,
  getSuggestedTargets,
  getMonthCustomerTargets,
  getTargetCoverage,
  getTargetReport,
  saveMonthCustomerTarget,
  savePersonnelTarget,
  updatePersonnelTarget,
} from "./targets";
import { targetsQueryKeys } from "./query-keys";
import { normalizeSuggestedTargets, normalizeTargetList } from "./transformers";

export function useGetTargetReport(params, enabled = true) {
  const hasParams =
    params?.level && params?.dc && params?.entityId && params?.year && params?.month;

  return useQuery({
    queryKey: targetsQueryKeys.report(params),
    queryFn: () =>
      getTargetReport(
        params.level,
        params.dc,
        params.entityId,
        params.year,
        params.month,
      ),
    enabled: enabled && !!hasParams,
  });
}

export function useGetPersonnelTargets(personnelId, enabled = true) {
  return useQuery({
    queryKey: targetsQueryKeys.personnel(personnelId),
    queryFn: () => getPersonnelTargets(personnelId),
    enabled: enabled && !!personnelId,
    select: normalizeTargetList,
  });
}

export function useGetSuggestedTargets(params, enabled = true) {
  const hasParams =
    params?.personnelId &&
    params?.manufacturerId &&
    params?.productGroupId &&
    params?.year &&
    params?.month;

  return useQuery({
    queryKey: targetsQueryKeys.suggested(params),
    queryFn: () =>
      getSuggestedTargets(
        params.personnelId,
        params.manufacturerId,
        params.productGroupId,
        params.year,
        params.month,
      ),
    enabled: enabled && !!hasParams,
    select: normalizeSuggestedTargets,
  });
}

export function useGetTargetCoverage(year, month, enabled = true, calendarType) {
  return useQuery({
    queryKey: targetsQueryKeys.coverage(year, month, calendarType),
    queryFn: () => getTargetCoverage(year, month, calendarType),
    enabled: enabled && !!year && !!month,
    select: (data) => (Array.isArray(data?.data) ? data.data : []),
  });
}

export function useGetManagerTargetDistribution(year, month, enabled = true, calendarType) {
  return useQuery({
    queryKey: targetsQueryKeys.distribution(year, month, calendarType),
    queryFn: () => getManagerTargetDistribution(year, month, calendarType),
    enabled: enabled && !!year && !!month,
  });
}

export function useGetMonthCustomerTargets(personnelId, year, month, enabled = true) {
  return useQuery({
    queryKey: targetsQueryKeys.monthCustomer(personnelId, year, month),
    queryFn: () => getMonthCustomerTargets(personnelId, year, month),
    enabled: enabled && !!personnelId && !!year && !!month,
    select: (data) => (Array.isArray(data?.data) ? data.data : []),
  });
}

export function useSaveMonthCustomerTargetMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveMonthCustomerTarget,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...targetsQueryKeys.all, "month-customer"],
      });
      queryClient.invalidateQueries({
        queryKey: [...targetsQueryKeys.all, "distribution"],
      });
    },
  });
}

export function useSavePersonnelTargetMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: savePersonnelTarget,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: targetsQueryKeys.personnel(variables.personnel_id),
      });
      queryClient.invalidateQueries({ queryKey: [...targetsQueryKeys.all, "coverage"] });
      queryClient.invalidateQueries({ queryKey: [...targetsQueryKeys.all, "distribution"] });
    },
  });
}


export function useUpdatePersonnelTargetMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, body }) => updatePersonnelTarget(uid, body),
    onSuccess: (_, variables) => {
      const personnelId = variables?.body?.personnel_id;
      if (personnelId) {
        queryClient.invalidateQueries({
          queryKey: targetsQueryKeys.personnel(personnelId),
        });
      }
      queryClient.invalidateQueries({ queryKey: [...targetsQueryKeys.all, "coverage"] });
      queryClient.invalidateQueries({ queryKey: [...targetsQueryKeys.all, "distribution"] });
    },
  });
}

export function useDeletePersonnelTargetMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, personnelId }) => deletePersonnelTarget(uid),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: targetsQueryKeys.personnel(variables.personnelId),
      });
      queryClient.invalidateQueries({ queryKey: [...targetsQueryKeys.all, "coverage"] });
      queryClient.invalidateQueries({ queryKey: [...targetsQueryKeys.all, "distribution"] });
    },
  });
}
